import React from "react";
import "./Glossary.css";

type GlossaryKey =
  | "attachment"
  | "application"
  | "pipeline"
  | "pace"
  | "tta"
  | "baseline"
  | "fsa"
  | "oht"
  | "scope"
  | "unattached"
  | "declaredCapacity"
  | "usedCapacity"
  | "remainingCapacity"
  | "decline"
  | "expire"
  | "acknowledge";

const GLOSSARY: Record<GlossaryKey, string> = {
  attachment:
    "An attachment is when a patient is formally matched to a family doctor and becomes their patient. \"Attachments completed\" means the matching process is fully done.",
  application:
    "An application is when a patient submits a request to be matched with a family doctor in this OHT. One patient = one application.",
  pipeline:
    "The pipeline is the set of applications that have been submitted but not yet attached. Big pipeline = lots of people still waiting.",
  pace:
    "Pace is how fast attachments are happening right now — measured as patients attached per day over the last 30 days.",
  tta:
    "Time to Attachment (TTA) is the average number of days between when a patient submits their application and when they are attached to a doctor.",
  baseline:
    "Baseline filters who counts in the numbers: 'All patients' = everyone, 'Unattached only' = people not yet attached, 'Attached' = people who were successfully attached.",
  fsa:
    "FSA = Forward Sortation Area — the first three characters of a Canadian postal code (e.g. M4M). Each clinic belongs to an OHT based on its FSA.",
  oht:
    "OHT = Ontario Health Team. A geographically-bounded network of clinics that coordinate primary care for a specific set of FSAs.",
  scope:
    "Scope is the set of clinics + doctors that an OHT Admin can see. It's automatically enforced by the backend based on the OHT's FSA coverage.",
  unattached:
    "Unattached means a patient has applied but isn't yet matched to a doctor.",
  declaredCapacity:
    "Declared capacity is the number of patients a doctor says they can take on. Not yet tracked in the system (backend pending).",
  usedCapacity:
    "Used capacity is the number of patients already attached to a doctor. This is derived from attachment records.",
  remainingCapacity:
    "Remaining capacity = declared capacity minus used capacity. Not yet calculable (backend pending).",
  decline:
    "A decline is when a doctor reviews a patient's application and chooses not to accept them.",
  expire:
    "An application expires if no decision is made within a set time window (typically 30 days).",
  acknowledge:
    "Acknowledge means a recipient clinic has confirmed they've seen an OHT-level message.",
};

type Props = {
  term: GlossaryKey;
  children?: React.ReactNode;
};

/** Tooltip icon next to a term. Renders the child text followed by a small (?) icon that shows a definition on hover. */
export function GlossaryTerm({ term, children }: Props) {
  return (
    <span className="oht-glossary-term">
      {children}
      <span className="oht-glossary-icon" tabIndex={0} aria-label={`Definition of ${children}`}>
        ?
        <span className="oht-glossary-tip" role="tooltip">
          {GLOSSARY[term]}
        </span>
      </span>
    </span>
  );
}

/** Standalone info icon — same tooltip behavior but without wrapping a specific word. */
export function GlossaryIcon({ term }: { term: GlossaryKey }) {
  return (
    <span className="oht-glossary-icon oht-glossary-icon--standalone" tabIndex={0} aria-label={`Definition of ${term}`}>
      ?
      <span className="oht-glossary-tip" role="tooltip">
        {GLOSSARY[term]}
      </span>
    </span>
  );
}
